import { type ReactNode, type SyntheticEvent } from 'react';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { integrationConfigurationsQuery } from '@gonfalon/rest-api/queries/integrationConfigurations';
import type { JSONPatchOp } from '@gonfalon/rest-api/types';
import { noop } from 'lodash';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import type { MetricDataSource } from '../types';

import type { MetricDataSourceEditorForm } from './useMetricDataSourceForm';

export function focusCodeMirror(e?: SyntheticEvent<HTMLElement>) {
  const codeMirrorInstance = document.querySelector('.cm-content');

  e?.currentTarget.blur();

  if (codeMirrorInstance instanceof HTMLElement) {
    codeMirrorInstance?.focus();
  }
}

export function getDataSourceQuickEditSnapshot(
  dataSource:
    | Pick<MetricDataSource, 'name' | 'description' | 'archived' | 'maintainerId' | 'maintainer'>
    | Pick<MetricDataSourceEditorForm['dataSource'], 'name' | 'description' | 'archived' | 'maintainerId'>,
) {
  return {
    name: dataSource.name,
    description: dataSource.description ?? '',
    archived: dataSource.archived ?? false,
    maintainerId:
      'maintainer' in dataSource ? (dataSource.maintainerId ?? dataSource.maintainer?._id) : dataSource.maintainerId,
  };
}

// The Table input method stores a table name in the client-only `dbTable` field. The backend only
// understands `sqlQuery`, so resolve `dbTable` to `SELECT * FROM <dbTable>` before previewing or
// submitting. An explicit `sqlQuery` (Query input method) always takes precedence.
export const resolveDataSourceSqlQuery = (dataSource: { sqlQuery?: string; dbTable?: string }): string => {
  if (dataSource.sqlQuery?.trim()) {
    return dataSource.sqlQuery;
  }

  const dbTable = dataSource.dbTable?.trim();

  return dbTable ? `SELECT * FROM ${dbTable}` : '';
};

export const getDataSourceFormDefaultValues = (dataSource: MetricDataSource) => ({
  dataSource: {
    archived: dataSource.archived ?? false,
    key: dataSource.key,
    name: dataSource.name,
    description: dataSource.description ?? '',
    integrationConfig: {
      key: dataSource.integrationKey,
      environmentKey: dataSource.environmentKey,
      projectKey: dataSource._projectSummary?.key,
    },
    maintainerId: getDataSourceQuickEditSnapshot(dataSource).maintainerId,
    sqlQuery: dataSource.sqlQuery,
    columnMappings: {
      ...dataSource.columnMappings,
      contexts:
        dataSource.columnMappings?.contexts && Object.keys(dataSource.columnMappings.contexts).length > 0
          ? Object.entries(dataSource.columnMappings.contexts)
          : ([['', '']] as Array<[string, string]>),
    },
    tags: dataSource.tags,
  },
});

export const SubmitListener = ({ children }: { children: ReactNode }) => (
  // Listen for Cmd+Enter to submit the form
  <div
    onKeyDownCapture={(event) => {
      const form = event.currentTarget?.closest('form');

      if (event.key === 'Enter' && event.metaKey) {
        event.preventDefault();
        event.stopPropagation();

        if (form instanceof HTMLFormElement) {
          form.requestSubmit();
        }
      }
    }}
    style={{ display: 'contents' }}
  >
    {children}
  </div>
);

// Map a metric data source JSON patch instruction to a human-readable field name
export const getInstructionName = ({
  instruction,
}: {
  instruction: JSONPatchOp & { value: string | boolean | number };
}) => {
  if (instruction.path.startsWith('/columnMappings/contexts/')) {
    return 'Context mapping';
  }

  switch (instruction.path) {
    case '/name':
      return 'Name';
    case '/description':
      return 'Description';
    case '/maintainerId':
      return 'Maintainer';
    case '/archived':
      if (instruction?.value as boolean) {
        return 'Archived';
      } else {
        return 'Unarchived';
      }
    case '/sqlQuery':
      return 'SQL Query';
    case '/columnMappings/keyColumn':
      return 'Key column';
    case '/columnMappings/valueColumn':
      return 'Value column';
    case '/columnMappings/timestampColumn':
      return 'Timestamp column';
    default:
      return null;
  }
};

export const warehouseExperimentationConfigValuesSchema = z.object({
  selectedEnv: z.object({
    environmentId: z.string(),
    environmentKey: z.string(),
    projectId: z.string(),
    projectKey: z.string(),
  }),
});

export const warehouseExperimentationConfigValuesMatchingSchema = z.object({
  selectedEnv: z.object({
    environmentKey: z.string(),
    projectKey: z.string(),
  }),
});

export const getIntegrationFieldDefaultValue = async ({ searchParams }: { searchParams: URLSearchParams }) => {
  const integrationKey = searchParams.get('integrationKey');
  const projectKey = searchParams.get('projectKey');
  const integrationConfigs =
    integrationKey && projectKey
      ? await getQueryClient().fetchQuery(integrationConfigurationsQuery({ integrationKey })).catch(noop)
      : null;
  const integrationConfigForProject = integrationConfigs?.items?.find((ic) => {
    const { data } = warehouseExperimentationConfigValuesSchema.safeParse(ic.configValues);

    return data?.selectedEnv?.projectKey === projectKey;
  });
  const defaultIntegrationConfig: MetricDataSourceEditorForm['dataSource']['integrationConfig'] | null =
    integrationConfigForProject
      ? (function () {
          const { data, success } = warehouseExperimentationConfigValuesSchema.safeParse(
            integrationConfigForProject.configValues,
          );

          if (
            success &&
            typeof integrationConfigForProject?._id === 'string' &&
            typeof integrationConfigForProject?._integrationKey === 'string'
          ) {
            const { selectedEnv } = data;

            return {
              environmentKey: selectedEnv.environmentKey,
              projectKey: selectedEnv.projectKey,
              key: integrationConfigForProject._integrationKey,
              id: integrationConfigForProject._id,
            };
          }

          return null;
        })()
      : null;

  return defaultIntegrationConfig;
};
